import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Skeleton, Space, Switch } from 'antd';
import { PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getVisibleTrips, pushTripsToBackend, tripWebHref } from '../../services/tripsService';
import {
  loadLocalUserPreferences,
  patchAppUserSettings,
} from '../../services/userPreferencesSyncService';
import type { Trip } from '../../types/client';
import { tripsEn } from '../../content/trips.en';
import { tripShareEn } from '../../content/tripShare.en';
import { parseTripStartLocal } from '../../utils/cruiseCountdownCalculator';
import CruiseCountdownCard from '../../components/CruiseCountdownCard/CruiseCountdownCard';
import TripListCard from '../../components/TripListCard/TripListCard';
import styles from './Trips.module.css';

const today = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isPastTrip = (trip: Trip): boolean => {
  const end = trip.endDate ?? trip.startDate;
  const start = trip.startDate ?? trip.endDate;
  if (!end && !start) return false;
  const now = today();
  return !!end && end < now && (!start || start < now);
};

const tripStartMs = (trip: Trip): number | null => {
  if (!trip.startDate) return null;
  const date = parseTripStartLocal(trip.startDate);
  return date ? date.getTime() : null;
};

const Trips: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, isDataSyncing } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  /** Inverse of hidePastTrips — Switch "on" means past trips are visible. */
  const [showPast, setShowPast] = useState(
    () => !loadLocalUserPreferences().appUserSettings.hidePastTrips
  );
  const hasPushedRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.sub && !isDataSyncing) {
      try {
        const list = getVisibleTrips(user.sub);
        const sorted = [...list].sort((a, b) => {
          const aStart = a.startDate ?? '';
          const bStart = b.startDate ?? '';
          return aStart.localeCompare(bStart);
        });
        setTrips(sorted);
        setShowPast(!loadLocalUserPreferences().appUserSettings.hidePastTrips);
        if (!hasPushedRef.current) {
          hasPushedRef.current = true;
          pushTripsToBackend(user.sub);
        }
      } catch {
        setTrips([]);
      }
    } else if (!isAuthenticated) {
      setTrips([]);
    }
  }, [isAuthenticated, user?.sub, isDataSyncing]);

  const handleShowPastChange = (checked: boolean): void => {
    setShowPast(checked);
    patchAppUserSettings({ hidePastTrips: !checked });
  };

  const displayed = useMemo(
    () => (showPast ? trips : trips.filter((t) => !isPastTrip(t))),
    [showPast, trips]
  );

  const nextUpcoming = useMemo(() => {
    const now = Date.now();
    let best: Trip | null = null;
    let bestStart = Number.POSITIVE_INFINITY;
    for (const trip of displayed) {
      const start = tripStartMs(trip);
      if (start == null || start <= now) continue;
      if (start < bestStart) {
        best = trip;
        bestStart = start;
      }
    }
    return best;
  }, [displayed]);

  if (authLoading || isDataSyncing) {
    return (
      <div className={styles.trips}>
        <div className={styles.container}>
          <div className={styles.header}>
            <Skeleton.Input active style={{ width: 120 }} />
            <Skeleton.Button active />
          </div>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.trips}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>{tripsEn.pageTitle}</h1>
            <Switch
              className={styles.pastSwitch}
              checked={showPast}
              onChange={handleShowPastChange}
              checkedChildren={tripsEn.showPast}
              unCheckedChildren={tripsEn.hidePast}
            />
          </div>
          <Space>
            <Button icon={<TeamOutlined />} onClick={() => navigate('/trips/shared')}>
              {tripShareEn.openInbox}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/trips/new')}>
              {tripsEn.addTrip}
            </Button>
          </Space>
        </div>

        {trips.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{tripsEn.noTrips}</div>
            <div className={styles.emptyDescription}>{tripsEn.noTripsDescription}</div>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/trips/new')}>
              {tripsEn.addTrip}
            </Button>
          </div>
        ) : displayed.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{tripsEn.filteredOutPast}</div>
            <Button onClick={() => handleShowPastChange(true)}>{tripsEn.showPastLabel}</Button>
          </div>
        ) : (
          <div className={styles.list}>
            {nextUpcoming?.startDate ? (
              <CruiseCountdownCard
                tripId={user?.sub ? tripWebHref(user.sub, nextUpcoming).slice('/trips/'.length) : nextUpcoming.id}
                tripName={nextUpcoming.title?.trim() || tripsEn.untitledTrip}
                tripType={nextUpcoming.tripType}
                startDate={nextUpcoming.startDate}
              />
            ) : null}
            {displayed.map((trip) => (
              <TripListCard key={trip.id} trip={trip} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Trips;
